<template>
  <div class="section-wrapper">
    <section
      class="privacy-section"
      id="privacy"
      aria-label="Privacy Policy section"
    >
      <div class="background-elements">
        <div class="grid-overlay"></div>
        <div class="floating-elements" aria-hidden="true">
          <div class="tech-circle"></div>
          <div class="tech-square"></div>
        </div>
      </div>

      <div class="content-container">
        <div class="policy-content">
          <div class="brand-tag slide-in" data-delay="100">
            <div class="tag-dot"></div>
            <span>Privacy Policy</span>
          </div>

          <h2 class="main-heading">
            <div class="heading-line fade-in" data-delay="300">
              Your Privacy
            </div>
            <div class="heading-highlight fade-in" data-delay="500">
              Matters to Us
              <svg
                class="heading-accent"
                viewBox="0 0 100 100"
                width="80"
                height="80"
              >
                <circle cx="50" cy="50" r="40" class="accent-circle" />
              </svg>
            </div>
          </h2>

          <div class="policy-text fade-in" data-delay="700">
            <p><strong>Last Updated: August 30, 2025</strong></p>
            <p>
              VisionLab Studio ("we," "us," or "our") is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your personal information when you visit
              our website (vision-lab.studio), use our services, or interact
              with us through our contact channels, including email (<a
                href="mailto:s.michael@vision-lab.studio"
                >s.michael@vision-lab.studio</a
              >) or phone (<a href="tel:+14244734444">(424) 473-4444</a>). By
              accessing our website or services, you agree to the terms outlined
              in this Privacy Policy.
            </p>

            <h3>1. Information We Collect</h3>
            <p>We may collect the following types of information:</p>
            <h4>a. Personal Information</h4>
            <ul>
              <li>
                <strong>Contact Information</strong>: Name, email address, phone
                number, company website, or other details you provide when you
                fill out our contact form, request a consultation, or contact us
                directly.
              </li>
              <li>
                <strong>File Uploads</strong>: Documents or files (e.g., PDFs,
                images, or text files) you upload through our contact form for
                project inquiries.
              </li>
              <li>
                <strong>Service Preferences</strong>: Information about the
                types of services you select (e.g., Enterprise Solutions, Custom
                Development, UI/UX Design) when interacting with our website.
              </li>
            </ul>
            <h4>b. Non-Personal Information</h4>
            <ul>
              <li>
                <strong>Usage Data</strong>: Information about how you interact
                with our website, such as IP address, browser type, pages
                visited, and time spent on the site, collected via cookies or
                similar technologies.
              </li>
              <li>
                <strong>Device Information</strong>: Details about the device
                you use to access our website, including operating system and
                browser version.
              </li>
            </ul>

            <h3>2. How We Use Your Information</h3>
            <p>We use your information to:</p>
            <ul>
              <li>
                Respond to your inquiries and provide information about our
                services, such as Enterprise Solutions, Custom Development, and
                Digital Transformation.
              </li>
              <li>
                Process and manage project requests, including reviewing
                uploaded files and service selections.
              </li>
              <li>Improve our website and services by analyzing usage data.</li>
              <li>
                Communicate with you about updates, promotions, or new services
                (you may opt out of marketing communications).
              </li>
              <li>
                Comply with legal obligations or protect our legal rights.
              </li>
            </ul>

            <h3>3. How We Share Your Information</h3>
            <p>
              We do not sell or rent your personal information. We may share
              your information with:
            </p>
            <ul>
              <li>
                <strong>Service Providers</strong>: Third-party vendors who
                assist us in operating our website, processing inquiries, or
                delivering services (e.g., cloud storage or analytics
                providers).
              </li>
              <li>
                <strong>Legal Authorities</strong>: If required by law or to
                protect the rights, property, or safety of VisionLab Studio, our
                users, or others.
              </li>
            </ul>

            <h3>4. Cookies and Tracking Technologies</h3>
            <p>
              We use cookies and similar technologies to enhance your
              experience, analyze website performance, and personalize content.
              You can manage cookie preferences through your browser settings.
            </p>

            <h3>5. Data Security</h3>
            <p>
              We implement reasonable security measures to protect your
              information, such as encryption and secure servers. However, no
              method of transmission over the internet is completely secure, and
              we cannot guarantee absolute security.
            </p>

            <h3>6. Your Rights</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access, correct, or delete your personal information.</li>
              <li>Opt out of marketing communications.</li>
              <li>Request information about how your data is processed.</li>
            </ul>
            <p>
              To exercise these rights, contact us at
              <a href="mailto:s.michael@vision-lab.studio"
                >s.michael@vision-lab.studio</a
              >
              or call (424) 473-4444.
            </p>

            <h3>7. Third-Party Links</h3>
            <p>
              Our website may contain links to third-party websites. We are not
              responsible for the privacy practices or content of those sites.
              We encourage you to review their privacy policies.
            </p>

            <h3>8. Changes to This Privacy Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated "Last Updated" date. We
              encourage you to review this policy periodically.
            </p>

            <h3>9. Contact Us</h3>
            <p>
              If you have questions about this Privacy Policy or our data
              practices, please contact us:
            </p>
            <ul>
              <li>
                <strong>Email</strong>:
                <a href="mailto:s.michael@vision-lab.studio"
                  >s.michael@vision-lab.studio</a
                >
              </li>
              <li>
                <strong>Phone</strong>:
                <a href="tel:+14244734444">(424) 473-4444</a>
              </li>
              <li>
                <strong>Address</strong>: VisionLab Studio, Los Angeles, CA
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted } from "vue";

export default {
  name: "PrivacyPolicy",
  setup() {
    onMounted(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const delay = entry.target.dataset.delay || 0;
              setTimeout(() => {
                entry.target.classList.add("visible");
              }, delay);
            }
          });
        },
        { threshold: 0.1 }
      );

      document.querySelectorAll(".fade-in, .slide-in").forEach((el) => {
        observer.observe(el);
      });

      return () => observer.disconnect();
    });
  },
};
</script>

<style lang="scss" scoped>
.section-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000000;
  position: relative;
  overflow: hidden;
  padding: 2rem 0;
}

.privacy-section {
  --primary-color: #6366f1;
  --accent-color: #818cf8;
  --text-primary: #ffffff;
  --text-secondary: #e2e8f0;
  --error-color: #ef4444;
  --success-color: #10b981;
  --warning-color: #f59e0b;

  width: 100%;
  padding: 4rem 0;
}

.background-elements {
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(#3d73ff08 1px, transparent 1px),
    linear-gradient(90deg, #3d73ff08 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
}

.content-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
}

.policy-content {
  max-width: 800px;
  margin: 0 auto;
}

.brand-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 100px;
  margin-bottom: 2rem;

  .tag-dot {
    width: 8px;
    height: 8px;
    background: var(--accent-color);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  span {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--text-primary);
    letter-spacing: 0.05em;
  }
}

.main-heading {
  margin-bottom: 3rem;

  .heading-line {
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .heading-highlight {
    position: relative;
    font-size: clamp(2.5rem, 5vw, 4rem);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;

    .heading-accent {
      position: absolute;
      top: -20px;
      right: -40px;
      z-index: -1;

      .accent-circle {
        fill: none;
        stroke: var(--accent-color);
        stroke-width: 2;
        stroke-dasharray: 251;
        stroke-dashoffset: 251;
        animation: drawCircle 2s ease forwards;
      }
    }
  }
}

.policy-text {
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;

  p {
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 2rem 0 1rem;
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--text-primary);
    margin: 1.5rem 0 0.5rem;
  }

  ul {
    list-style: disc;
    margin-left: 1.5rem;
    margin-bottom: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }

  a {
    color: var(--primary-color);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: var(--accent-color);
    }
  }
}

.fade-in {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in {
  opacity: 0;
  transform: translateX(-20px);
  transition: all 0.6s cubic-bezier(0.22, 1, 0.36, 1);

  &.visible {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

@media (max-width: 768px) {
  .privacy-section {
    padding: 2rem 0;
  }

  .section-wrapper {
    padding: 1rem 0;
  }

  .main-heading {
    .heading-highlight {
      font-size: clamp(2rem, 4vw, 3rem);
    }
  }

  .policy-text {
    font-size: 0.95rem;

    h3 {
      font-size: 1.3rem;
    }

    h4 {
      font-size: 1.1rem;
    }
  }
}
</style>
